import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import useStickyIntersection from '../../hooks/useStickyIntersection';
import CategoryGridWrapper from '../CategoryGridWrapper/CategoryGridWrapper';
import GroupedCategoryGridWrapper from '../GroupedCategoryGridWrapper';
import SkeletonProductGrid from '../ProductGrid/SkeletonGrid';
import CategoryGridHeader from '../ProductGridHeader/CategoryGridHeader/CategoryGridHeader';
import ProductGridScrollHandler from '../ProductGridScrollHandler';
import StickyRefineBar from '../Refine/StickyRefineBar';
import ScrollToTop from '../ScrollToTop/ScrollToTop';
import searchPageStyle from '../SearchMainContent/SearchMainContent.module.scss';
import {
  categoryDataType, groupedCategoryDataType,
} from '../types';
import style from './CategoryPage.module.scss';
import CategoryPageContext from './CategoryPageContext';
import CategoryPageESpotContent from './CategoryPageESpotContent';

const Main = ({
  brand,
  categoryData,
  categoryDataRefetch,
  excludeCategoryHeroEspot,
  grouped,
  groupedCategoryData,
  handleCheckBoxChange,
  handleSortChange,
  hasCatalogMfeEspots,
  intlCategoryId,
  isCategoryDataLoading,
  isUnifiedCategoryPage,
  onClearAllBtnClick,
  onClearFacetTag,
  onPaginationButtonClick,
  parameters,
  seqSlot,
  store,
  storeDetails,
  storeId,
  sortData = { defaultSortOption: '', sortOptions: [] },
  breadcrumbTrailEntryView,
  fullCatHead,
}) => {
  const gridWrapper = useRef();
  const [productTotalCount, setProductTotalCount] = useState(0);
  const { width } = useWindowSize();
  const gridIntersecting = useStickyIntersection(gridWrapper);

  const {
    facets: facetData,
    navParentCategoryId,
    name,
    title,
    url,
    espotContent,
    espotIdentifier,
    pagination: { currentPage, totalPages },
    products,
    productTotalCount: fetchedProductTotalCount,
  } = categoryData;

  const {
    categoryId,
    facet,
    filter,
    rows,
    sort,
    start,
  } = parameters;
  const isFacetSelected = parameters.facet?.length > 0;
  const isDesktop = width > 1025;
  const isStickyBarShowing = gridIntersecting && !isDesktop;

  const handleProductCount = (count) => {
    if (count !== undefined) {
      setProductTotalCount(count);
      window.dispatchEvent(new CustomEvent('mfe:updateProductCount', { detail: count }));
    }
  };

  // Category/Group Category Page Grid Content
  const categoryPageGridContent = (() => {
    if (isCategoryDataLoading) {
      return <SkeletonProductGrid cardAmount={12} />;
    }

    return grouped
      ? (
        <GroupedCategoryGridWrapper
          key={`${categoryId}-Grouped-Page`}
          categoryId={categoryId}
          facet={facet}
          filter={filter}
          fullCatHead={fullCatHead}
          groupedCategoryData={groupedCategoryData}
          groupedCategoryDataRefetch={categoryDataRefetch}
          handleProductCount={handleProductCount}
          isGroupedCategoryDataLoading={isCategoryDataLoading}
          isShopMyStore={parameters.filter !== ''}
          onPaginationButtonClick={onPaginationButtonClick}
          rows={rows}
          seqSlot={seqSlot}
          sort={sort}
          start={start}
        />
      ) : (
        <CategoryGridWrapper
          key={`${categoryId}-Page`}
          categoryDataRefetch={categoryDataRefetch}
          categoryId={categoryId}
          currentPage={currentPage}
          espotContent={espotContent}
          espotIdentifier={espotIdentifier}
          fullCatHead={fullCatHead}
          handleProductCount={handleProductCount}
          isCategoryDataLoading={isCategoryDataLoading}
          onPaginationButtonClick={onPaginationButtonClick}
          products={products}
          productTotalCount={fetchedProductTotalCount}
          rows={rows}
          start={start}
          totalPages={totalPages}
        />
      );
  })();

  const categoryPageMainComponent = (
    <CategoryPageContext store={store} storeId={storeId}>
      <CategoryPageESpotContent
        brand={brand}
        categoryId={intlCategoryId}
        excludeCategoryHeroEspot={excludeCategoryHeroEspot}
        hasCatalogMfeEspots={hasCatalogMfeEspots}
      >
        <ProductGridScrollHandler parameters={parameters}>
          <div className={searchPageStyle.gridHeaderPlaceholder}>
            <CategoryGridHeader
              brand={brand}
              categoryId={intlCategoryId}
              categoryName={name}
              categoryTitle={title}
              categoryUrl={url}
              facet={facet}
              facetData={facetData}
              filter={filter}
              hasSelectedCategoryFilters={filter !== '' || facet?.length > 0}
              navParentCategoryId={navParentCategoryId}
              onCheckBoxChange={handleCheckBoxChange}
              onClearAllBtnClick={onClearAllBtnClick}
              onFacetTagClick={onClearFacetTag}
              onSortChange={handleSortChange}
              resultsCount={productTotalCount}
              selectedSort={sort || sortData.defaultSortOption}
              sortData={sortData}
              storeDetails={storeDetails}
            />
          </div>
          <div ref={gridWrapper}>
            {isStickyBarShowing ? (
              <StickyRefineBar
                brand={brand}
                breadcrumbTrailEntryView={breadcrumbTrailEntryView}
                facetData={facetData}
                isCategoryPage
                isFacetSelected={isFacetSelected}
                onCheckBoxChange={handleCheckBoxChange}
                onClearAllBtnClick={onClearAllBtnClick}
                onSortChange={handleSortChange}
                resultsCount={productTotalCount}
                selectedSort={parameters.sort || sortData.defaultSortOption}
                sortData={sortData}
              />
            ) : null}
            {categoryPageGridContent}
            {isUnifiedCategoryPage ? <ScrollToTop /> : null}
          </div>
        </ProductGridScrollHandler>
      </CategoryPageESpotContent>
    </CategoryPageContext>
  );

  return isUnifiedCategoryPage
    ? (
      <main className={style.row_right}>
        {categoryPageMainComponent}
      </main>
    )
    : categoryPageMainComponent;
};

Main.propTypes = {
  brand: PropTypes.string.isRequired,
  categoryData: categoryDataType.isRequired,
  excludeCategoryHeroEspot: PropTypes.bool.isRequired,
  grouped: PropTypes.bool.isRequired,
  groupedCategoryData: groupedCategoryDataType,
  isCategoryDataLoading: PropTypes.bool.isRequired,
  categoryDataRefetch: PropTypes.func.isRequired,
  handleCheckBoxChange: PropTypes.func.isRequired,
  handleSortChange: PropTypes.func.isRequired,
  hasCatalogMfeEspots: PropTypes.bool.isRequired,
  intlCategoryId: PropTypes.string.isRequired,
  isUnifiedCategoryPage: PropTypes.bool.isRequired,
  onClearAllBtnClick: PropTypes.func.isRequired,
  onClearFacetTag: PropTypes.func.isRequired,
  onPaginationButtonClick: PropTypes.func.isRequired,
  parameters: PropTypes.shape({
    categoryId: PropTypes.string,
    facet: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    filter: PropTypes.string,
    rows: PropTypes.string,
    sort: PropTypes.string,
    start: PropTypes.string,
  }).isRequired,
  sortData: PropTypes.shape({
    defaultSortOption: PropTypes.string,
    sortOptions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        value: PropTypes.string,
      }),
    ),
  }),
  breadcrumbTrailEntryView: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  fullCatHead: PropTypes.bool,
  seqSlot: PropTypes.string.isRequired,
  store: PropTypes.string.isRequired,
  storeDetails: PropTypes.shape({
    storeName: PropTypes.string,
    storeId: PropTypes.string,
  }),
  storeId: PropTypes.string.isRequired,
};

export default Main;
